/**
 * Contacts client helpers, shared between the /contacts page and the dock's
 * contact inspector panel (2026-08-28 — the inspector is a DOCK panel per
 * the owner's directive, so this can no longer live inside the page).
 */

export interface ContactCard {
	slug: string;
	title: string;
	values: Record<string, unknown>;
	body_preview?: string;
}

export interface ContactForm {
	name: string;
	email: string;
	phone: string;
	company: string;
	role: string;
	birthday: string;
	category: string;
	nickname: string;
	pronouns: string;
	relationship: string;
	address: string;
	website: string;
	notes: string;
}

export const EMPTY_CONTACT_FORM: ContactForm = {
	name: "", email: "", phone: "", company: "", role: "", birthday: "",
	category: "", nickname: "", pronouns: "", relationship: "", address: "",
	website: "", notes: "",
};

/** String value of one card property ("" for missing/null). */
export function contactValue(card: Pick<ContactCard, "values">, key: string): string {
	const raw = card.values?.[key];
	return raw === null || raw === undefined ? "" : String(raw);
}

/** Error detail from our API: FastAPI nests either a string or a
 * {error, message} object under `detail` — surface the message, never
 * "[object Object]" (Rule 26). */
export function apiError(payload: unknown, fallback: string): string {
	if (payload && typeof payload === "object" && "detail" in payload) {
		const d = (payload as { detail: unknown }).detail;
		if (typeof d === "string") return d;
		if (d && typeof d === "object" && "message" in d) {
			return String((d as { message: unknown }).message);
		}
	}
	return fallback;
}

/** The collections url type requires a scheme; "example.se" is
 * unambiguous intent — canonicalize instead of bouncing a 400. */
export function normalizeWebsite(raw: string): string {
	const clean = raw.trim();
	return clean && !clean.includes("://") ? `https://${clean}` : clean;
}

/** The category select's options from the contacts collection schema.
 * [] when the collection doesn't exist yet (or has no category field). */
export async function fetchCategoryOptions(): Promise<string[]> {
	try {
		const res = await fetch("/api/collections/contacts");
		if (!res.ok) return [];
		const body = await res.json();
		const cat = (body.properties ?? []).find(
			(p: { key?: string }) => p.key === "category",
		);
		return (cat?.options as string[] | undefined) ?? [];
	} catch {
		return [];
	}
}

/** Append a category option to the collection schema (fetched fresh so a
 * stale copy never clobbers other edits). Returns the error message on
 * failure, null on success — the caller owns the toast. */
export async function createCategoryOption(name: string): Promise<string | null> {
	const clean = name.trim();
	if (!clean) return "Category name is empty";
	try {
		const res = await fetch("/api/collections/contacts");
		if (!res.ok) return `Couldn't read the contacts schema (${res.status})`;
		const schema = await res.json();
		const props = (schema.properties ?? []) as Array<Record<string, unknown>>;
		const cat = props.find((p) => p.key === "category");
		if (!cat) return "The contacts collection has no category field";
		const opts = (cat.options as string[] | undefined) ?? [];
		if (!opts.includes(clean)) {
			cat.options = [...opts, clean];
			const patch = await fetch("/api/collections/contacts", {
				method: "PATCH",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ properties: props }),
			});
			if (!patch.ok) {
				const detail = await patch.json().catch(() => ({}));
				return apiError(detail, `Couldn't add the category (${patch.status})`);
			}
		}
		return null;
	} catch (e) {
		return e instanceof Error ? e.message : "Couldn't add the category";
	}
}

/** Fired (on window) whenever a contact card is created/edited/deleted from
 * ANY surface, so the /contacts page can reload its list. Same bus the
 * command palette uses (`agntspace:*` CustomEvents). */
export const CONTACTS_CHANGED_EVENT = "agntspace:contacts-changed";

export function emitContactsChanged(): void {
	window.dispatchEvent(new CustomEvent(CONTACTS_CHANGED_EVENT));
}
